import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/helpers/cn';

const buttonVariants = cva(
  'dark:text-gray-100 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/60 dark:hover:bg-primary/60',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline:
          'text-primary dark:text-gray-200 dark:bg-gray-800 border border-primary focus-visible:outline-hidden focus-visible:ring-0 bg-background dark:hover:bg-background/30 hover:bg-primary/10 hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/60',
        ghost:
          'dark:hover:text-gray-200 hover:text-gray-900 disabled:pointer-events-none',
        ghostScale:
          'dark:hover:text-gray-200 transform hover:scale-125 disabled:pointer-events-none',
        link: 'text-primary underline-offset-4 hover:underline',
        shadow:
          'items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-hidden focus-visible:outline-hidden focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground rounded-md px-3 text-xs h-8 dark:bg-gray-800',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  classNameIcon,
  variant,
  size,
  asChild = false,
  icon,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: React.ReactNode;
    classNameIcon?: string;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(
        'group cursor-pointer transition duration-500 ease-out',
        buttonVariants({ variant, size, className })
      )}
      {...props}
    >
      {icon && (
        <span
          className={cn(
            'material-symbols-outlined transition-transform duration-150 group-hover:scale-110',
            classNameIcon
          )}
        >
          {icon}
        </span>
      )}
      {/* Slot needs exactly one element to merge into: with asChild the
          child (e.g. a Link) is marked Slottable and the icon goes inside it.
          A bare `false` icon next to it made Slot throw
          "React.Children.only expected to receive a single React element". */}
      <Slottable>{props.children}</Slottable>
    </Comp>
  );
}

export { Button, buttonVariants };
